/**
 * ThemeProvider - Proveedor de contexto para el tema
 * 
 * Este componente carga el tema configurado y lo hace disponible
 * para toda la aplicación a través de React Context.
 * 
 * También se encarga de inyectar las variables CSS dinámicamente
 * en el elemento :root del documento.
 */

import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {ITheme} from 'themes/theme.types';
import {loadTheme} from 'themes';

interface ThemeContextType {
  theme: ITheme | null;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType>({ 
  theme: null, 
  isLoading: true 
});

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Convierte un nombre en camelCase a kebab-case
 * Ejemplo: textPrimary -> text-primary
 */
const camelToKebab = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase();
};

/**
 * Componente ThemeProvider
 * 
 * Debe envolver la aplicación completa para que todos los componentes
 * tengan acceso al tema.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<ITheme | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadTheme()
      .then(loadedTheme => {
        setTheme(loadedTheme);
        
        // Inyecta las variables CSS en el :root
        const root = document.documentElement;
        
        // Inyectar colores
        Object.entries(loadedTheme.colors).forEach(([key, value]) => {
          // Mapea camelCase (textPrimary) a kebab-case (--color-text-primary)
          const cssVarName = `--color-${camelToKebab(key)}`;
            console.log(cssVarName)
          root.style.setProperty(cssVarName, value);
        });
        
        // Inyectar tipografía
        root.style.setProperty('--font-family', loadedTheme.typography.fontFamily);
        
        // Actualizar el título de la página
        document.title = loadedTheme.content.global.appName;
        
        // Actualizar el favicon si está especificado
        const faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (faviconLink && loadedTheme.assets.favicon) {
          faviconLink.href = loadedTheme.assets.favicon;
        }
        
        console.log(`✅ Tema '${loadedTheme.brandName}' cargado exitosamente`);
      })
      .catch(error => {
        console.error("❌ Error al cargar el tema:", error);
        // En caso de error, la aplicación no se renderizará
        // Se podría implementar un fallback adicional aquí
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Mostrar un loader mientras se carga el tema
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'Arial, sans-serif',
        color: '#474747'
      }}>
        <div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={{ theme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Custom Hook para consumir el tema
 * 
 * Uso:
 * ```tsx
 * const { theme } = useTheme();
 * return <h1>{theme?.content.welcomePage.title}</h1>;
 * ```
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme debe ser usado dentro de un ThemeProvider');
  }
  
  return context;
};
